import { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import {
  Button,
  Divider,
  FormControl,
  FormLabel,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { ActivityStatusSelect } from '../status-select';
import { createActivitySchema, type ActivityResponseType } from './schema';
import { SearchCourseDialog } from './search-course-dialog';
import { ExternalResourceDialog } from '@/components/partial/external-resource-dialog';
import { BaseDialog } from '@/components/shared/base-dialog';
import { mapResourceType } from '@/constants/resources/map-resource-type';
import { ActivityType } from '@/data/idp/interfaces';
import { useUpdatePlanActivity } from '@/data/idp/plans/activities/update';
import type { UpdateActivityPayload } from '@/data/idp/plans/activities/update/interfaces';
import {
  type Activity,
  PlanStatus,
  type ExternalLink,
} from '@/data/idp/plans/interfaces';
import type { Course } from '@/models/catalog/course';
import { type EmbeddedCourse, type ExternalResource } from '@/models/resources';
import { RecommendedCoursesButton } from '@/packages/idp/components/recommended-courses-button';
import { RecommendedCoursesDialog } from '@/packages/idp/components/recommended-courses-dialog';
import { SelectedCourseCard } from '@/packages/idp/components/selected-course-card';
import { SelectedResourceCard } from '@/packages/idp/pages/show-plan/components/activity-list/components/activity-card/selected-resource-card';

interface LocalAttachedResource {
  course?: Course | EmbeddedCourse;
  external_link?: ExternalLink;
}

interface ActivityDialogProps {
  activity: Activity;
  onClose: () => void;
  planStatus: PlanStatus;
}

export const ActivityDialog = ({
  activity,
  onClose,
  planStatus,
}: ActivityDialogProps) => {
  const [showRecommendedCoursesDialog, setShowRecommendedCoursesDialog] =
    useState(false);
  const [showSearchCourseDialog, setShowSearchCourseDialog] = useState(false);
  const [showExternalResourceDialog, setShowExternalResourceDialog] =
    useState(false);

  const [attachedResource, setAttachedResource] =
    useState<LocalAttachedResource | null>(() => {
      if (activity?.attached_resource?.course) {
        return { course: activity.attached_resource.course };
      }
      if (activity?.attached_resource?.external_link) {
        return { external_link: activity.attached_resource.external_link };
      }
      return null;
    });

  const { mutate: updatePlanActivity, isPending } =
    useUpdatePlanActivity(activity);

  const { control, handleSubmit, reset } = useForm({
    resolver: zodResolver(createActivitySchema),
    defaultValues: {
      description: activity?.description || '',
      title: activity?.title || '',
    },
  });

  const activityType = activity?.recommended_activity?.activity_type;

  useEffect(() => {
    reset({
      description: activity?.description || '',
      title: activity?.title || '',
    });

    if (activity?.attached_resource?.course) {
      setAttachedResource({ course: activity.attached_resource.course });
    } else if (activity?.attached_resource?.external_link) {
      setAttachedResource({
        external_link: activity.attached_resource.external_link,
      });
    } else {
      setAttachedResource(null);
    }
  }, [activity, reset]);

  const handleSave = (data: ActivityResponseType) => {
    const payload: UpdateActivityPayload = {
      ...data,
      attached_resource: attachedResource,
    };

    updatePlanActivity(payload, {
      onSuccess: () => onClose(),
    });
  };

  const handleCourseSelect = (course: Course | EmbeddedCourse) => {
    setAttachedResource({ course });
  };

  const handleExternalResourceSelect = (resource: ExternalResource) => {
    setAttachedResource({
      external_link: {
        ...resource,
        category: mapResourceType[resource.category],
      },
    });
  };

  const handleRemoveResource = () => {
    setAttachedResource(null);
  };

  const hasAttachedResource =
    attachedResource?.course || attachedResource?.external_link;

  const getCourseDisplayData = (course: Course | EmbeddedCourse) => {
    if ('name' in course) {
      return course as EmbeddedCourse;
    }

    const catalogCourse = course as Course;
    return {
      id: String(catalogCourse.id),
      name: catalogCourse.course_name,
      institution_name: catalogCourse.institution?.name || '',
      course_type: catalogCourse.course_type?.type,
      external_institution_logo_url: catalogCourse.institution?.logo_url,
      external_institution_background_color:
        catalogCourse.institution?.logo_background_color,
      modality: catalogCourse.modalities[0].type,
      duration: catalogCourse.duration.quantity,
      duration_type: catalogCourse.duration.type,
    } as EmbeddedCourse;
  };

  const getCourseId = (course: Course | EmbeddedCourse) => {
    if ('external_course_id' in course && course.external_course_id) {
      return course.external_course_id;
    }
    return course.id;
  };

  return (
    <BaseDialog
      onClose={onClose}
      actionsComponent={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSubmit(handleSave)}
            loading={isPending}
          >
            Salvar
          </Button>
        </>
      }
    >
      {planStatus === PlanStatus.InProgress && (
        <ActivityStatusSelect activity={activity} planStatus={planStatus} />
      )}
      <form>
        <Stack gap={2} mt={2}>
          <Controller
            name="title"
            control={control}
            render={({ field, fieldState }) => {
              return (
                <FormControl fullWidth>
                  <FormLabel htmlFor="title">Nome da atividade</FormLabel>
                  <TextField
                    {...field}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    id="title"
                    placeholder="Dê um nome para a sua atividade"
                    variant="outlined"
                  />
                </FormControl>
              );
            }}
          />
          <Controller
            name="description"
            control={control}
            render={({ field }) => {
              return (
                <FormControl fullWidth>
                  <FormLabel htmlFor="description">
                    Descrição
                    <Typography variant="body2" component="span" ml={1}>
                      Opcional
                    </Typography>
                  </FormLabel>
                  <TextField
                    {...field}
                    id="description"
                    multiline
                    placeholder="Adicionar descrição"
                    rows={4}
                    variant="outlined"
                  />
                </FormControl>
              );
            }}
          />
          <Divider />

          {attachedResource?.course && (
            <>
              <SelectedCourseCard
                course={getCourseDisplayData(attachedResource.course)}
                removable
                onRemove={handleRemoveResource}
              />
              <Typography>
                Comece a estudar agora mesmo e faça a sua matrícula.{' '}
                <Link
                  href={`${window.location.origin}/cursos/${getCourseId(attachedResource.course)}`}
                  target="_blank"
                >
                  Ir para a página do curso
                </Link>
              </Typography>
            </>
          )}

          {attachedResource?.external_link && (
            <SelectedResourceCard
              resource={attachedResource.external_link}
              removable
              onRemove={handleRemoveResource}
            />
          )}

          {!hasAttachedResource && (
            <>
              <Stack gap={1}>
                <Typography variant="h3">Incluir curso</Typography>
                <Typography>Adicione um curso para sua atividade.</Typography>
              </Stack>
              {activityType === ActivityType.Course && (
                <RecommendedCoursesButton
                  courseType={activity?.recommended_activity?.course_type}
                  onClick={() => setShowRecommendedCoursesDialog(true)}
                  fullWidth
                />
              )}
              <Button
                variant="outlined"
                fullWidth
                sx={{ height: 46 }}
                startIcon={<SearchIcon />}
                onClick={() => setShowSearchCourseDialog(true)}
              >
                Buscar na plataforma
              </Button>
              <Button
                variant="outlined"
                fullWidth
                sx={{ height: 46 }}
                startIcon={<AddIcon />}
                onClick={() => setShowExternalResourceDialog(true)}
              >
                Adicionar recurso externo
              </Button>
            </>
          )}
        </Stack>
      </form>

      {showRecommendedCoursesDialog && (
        <RecommendedCoursesDialog
          activityId={activity.recommended_activity?.id}
          onClose={() => setShowRecommendedCoursesDialog(false)}
          onSave={(course) => {
            handleCourseSelect(course);
            setShowRecommendedCoursesDialog(false);
          }}
        />
      )}

      {showSearchCourseDialog && (
        <SearchCourseDialog
          onClose={() => setShowSearchCourseDialog(false)}
          onSave={(course) => {
            handleCourseSelect(course);
          }}
        />
      )}

      {showExternalResourceDialog && (
        <ExternalResourceDialog
          onClose={() => setShowExternalResourceDialog(false)}
          onSubmit={(resource) => {
            handleExternalResourceSelect(resource);
            setShowExternalResourceDialog(false);
          }}
          customHeader={
            <>
              <Typography variant="h2" mb={2}>
                Incluir recurso
              </Typography>
              <Typography color="text.secondary">
                Adicione um recurso para sua atividade. Você pode incluir
                diferentes recursos para sua atividade, é só categorizá-los pelo
                tipo.
              </Typography>
            </>
          }
        />
      )}
    </BaseDialog>
  );
};
